import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Panel } from "./usePlay";
import { api, errMsg, fmtAZN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BetControl } from "@/components/game/BetControl";
import { sfx } from "@/lib/sound";

const MULTS = [33, 11, 4, 2, 1.1, 0.5, 0.3, 0.5, 1.1, 2, 4, 11, 33];
const ROWS = 12, W = 600, S = W / (ROWS + 2), TOP = 40, GAP = 40;
const bucketColor = (m) => (m >= 11 ? "#c4112f" : m >= 4 ? "#ea580c" : m >= 2 ? "#d97706" : m >= 1 ? "#65a30d" : "#3f3f46");
const DUR = 2.6;

const Ball = ({ ball, onLand }) => {
  const xs = [W / 2], ys = [10];
  let sum = 0;
  ball.path.forEach((p, r) => { xs.push(W / 2 + (sum - r / 2) * S); ys.push(TOP + r * GAP - 12); sum += p; });
  xs.push(W / 2 + (ball.slot - 6) * S); ys.push(TOP + ROWS * GAP + 6);
  return (
    <motion.circle r="9" fill="url(#ballg)" initial={{ cx: xs[0], cy: ys[0] }} animate={{ cx: xs, cy: ys }}
      transition={{ duration: DUR, ease: "linear" }} onAnimationComplete={() => onLand(ball)} />
  );
};

export const PlinkoGame = ({ mode }) => {
  const { updateUser, refresh } = useAuth();
  const field = mode === "real" ? "balance" : "demo_balance";
  const inFlight = useRef(0);
  const [bet, setBet] = useState(1);
  const [balls, setBalls] = useState([]);
  const [hit, setHit] = useState(null);
  const [last, setLast] = useState([]);
  const [lastWin, setLastWin] = useState(null);

  const drop = async () => {
    let d;
    try { d = (await api.post("/games/plinko", { bet, mode })).data; } catch (e) { return toast.error(errMsg(e)); }
    inFlight.current += 1;
    updateUser((u) => ({ [field]: Math.round((u[field] - d.bet) * 100) / 100 }));
    const ball = { ...d, id: Math.random() };
    setBalls((b) => [...b, ball]);
    d.path.forEach((_, i) => setTimeout(sfx.peg, ((i + 1) / (ROWS + 2)) * DUR * 1000));
  };

  const land = (ball) => {
    setBalls((b) => b.filter((x) => x.id !== ball.id));
    setHit({ slot: ball.slot, k: ball.id });
    setLast((l) => [ball.multiplier, ...l].slice(0, 8));
    setLastWin(ball);
    inFlight.current -= 1;
    updateUser((u) => ({ [field]: Math.round((u[field] + ball.win) * 100) / 100 }));
    if (inFlight.current === 0) refresh();
    (ball.multiplier >= 2 ? sfx.win : ball.multiplier >= 1 ? sfx.cashout : sfx.lose)();
  };

  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-6" data-testid="plinko-game">
      <Panel className="order-2 lg:order-1">
        <div><p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Mərc</p><BetControl value={bet} onChange={setBet} testid="plinko-bet" /></div>
        <button onClick={drop} className="btn-gold w-full rounded-full py-4 font-display font-bold" data-testid="plinko-drop-btn">Topu burax</button>
        <p className="text-xs text-zinc-500">12 sıra · orta risk · bir neçə top eyni anda buraxıla bilər</p>
        <div className="flex flex-wrap gap-1.5" data-testid="plinko-history">
          {last.map((m, i) => <span key={i} className="rounded-md px-2 py-1 text-xs font-bold" style={{ background: bucketColor(m) }}>x{m}</span>)}
        </div>
      </Panel>
      <div className="machine-frame rounded-3xl p-3 sm:p-6 order-1 lg:order-2">
        <svg viewBox={`0 0 ${W} ${TOP + ROWS * GAP + 50}`} className="w-full max-h-[68vh] mx-auto" data-testid="plinko-board">
          <defs>
            <radialGradient id="ballg" cx="35%" cy="35%"><stop offset="0%" stopColor="#fff3b0" /><stop offset="50%" stopColor="#FFD700" /><stop offset="100%" stopColor="#a07c1c" /></radialGradient>
          </defs>
          {Array.from({ length: ROWS }, (_, r) => Array.from({ length: r + 3 }, (_, k) => (
            <circle key={`${r}-${k}`} cx={W / 2 + (k - (r + 2) / 2) * S} cy={TOP + r * GAP} r="4.5" fill="#e5e7eb" style={{ filter: "drop-shadow(0 0 3px rgba(255,255,255,0.6))" }} />
          )))}
          {MULTS.map((m, i) => {
            const x = W / 2 + (i - 6) * S;
            const isHit = hit?.slot === i;
            return (
              <motion.g key={`${i}-${isHit ? hit.k : ""}`} initial={isHit ? { y: 8 } : false} animate={{ y: 0 }} transition={{ type: "spring", stiffness: 400 }}>
                <rect x={x - S / 2 + 2} y={TOP + ROWS * GAP + 8} width={S - 4} height="30" rx="6" fill={bucketColor(m)} stroke={isHit ? "#fff" : "none"} strokeWidth="2" />
                <text x={x} y={TOP + ROWS * GAP + 27} fill="#fff" fontSize="12" fontWeight="800" textAnchor="middle" style={{ fontFamily: "Unbounded" }}>{`${m}x`}</text>
              </motion.g>
            );
          })}
          {balls.map((b) => <Ball key={b.id} ball={b} onLand={land} />)}
        </svg>
        {lastWin && <p className="text-center text-sm text-zinc-400 mt-2" data-testid="plinko-last">Son: x{lastWin.multiplier} · {fmtAZN(lastWin.win)}</p>}
      </div>
    </div>
  );
};
